// React provider pro navigační kernel

import { createContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { NavigationKernel, createNavigationKernel } from '../core/navigationKernel';
import { createNavigationStore, type NavigationStore } from '../core/navigationStore';
import type { NavigationState } from '../core/navigationState';

// Lokalizace je registrována přes veřejný i18n manifest (navigationI18nManifest),
// ne module-level side effectem

export interface NavigationContextValue {
  readonly kernel: NavigationKernel;
  readonly store: NavigationStore;
}

export const NavigationContext = createContext<NavigationContextValue | null>(null);

interface NavigationProviderProps {
  children: ReactNode;
  kernel?: NavigationKernel;
}

/**
 * Provider pro navigační jádro.
 * Vloží kernel a Zustand store do React kontextu.
 */
export function NavigationProvider({ children, kernel: externalKernel }: NavigationProviderProps) {
  const kernelRef = useRef<NavigationKernel>(null);
  if (!kernelRef.current) {
    kernelRef.current = externalKernel ?? createNavigationKernel();
  }

  const storeRef = useRef<NavigationStore>(null);
  if (!storeRef.current) {
    storeRef.current = createNavigationStore();
  }

  const kernel = kernelRef.current;
  const store = storeRef.current;

  // Synchronizace kernel stavu do Zustand store
  useEffect(() => {
    const unsubscribe = kernel.subscribe((state: NavigationState) => {
      store.setState({ navigation: state });
    });

    // Inicializuj store s aktuálním stavem kernelu
    store.setState({ navigation: kernel.getState() });

    return unsubscribe;
  }, [kernel, store]);

  const contextValue = useMemo<NavigationContextValue>(() => ({ kernel, store }), [kernel, store]);

  return <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>;
}
